import { AutocompleteProvider } from "../../../types/enums";
import { LatLng } from "../../maps/map-provider.interface";
import {
    AddressSuggestOptions,
    AddressSuggestion,
    GeoAddress,
    IAddressSuggestProvider,
    IGeocodingProvider
} from "../address-search-provider.interface";
import { fetchJsonAsync } from "../geo-search-fetch";
import { toStreetShortName } from "../street-short-name";
import { toAlpha2CountryCode, toAlpha3CountryCodes } from "./here-address-format";
import {
    HereAddress,
    HereAutocompleteItem,
    HereAutosuggestItem,
    HereGeocodeItem,
    HereLookupResponse,
    HereRevGeocodeItem
} from "./here-api.interface";

const AUTOSUGGEST_URL = "https://autosuggest.search.hereapi.com/v1/autosuggest";
const AUTOCOMPLETE_URL = "https://autocomplete.search.hereapi.com/v1/autocomplete";
const LOOKUP_URL = "https://lookup.search.hereapi.com/v1/lookup";
const GEOCODE_URL = "https://geocode.search.hereapi.com/v1/geocode";
const REVERSE_GEOCODE_URL = "https://revgeocode.search.hereapi.com/v1/revgeocode";

/**
 * Autosuggest matches a bare house number ("996") against nearby streets, which autocomplete
 * cannot do at any radius, and it tolerates misspellings. Set to false to fall back to the
 * autocomplete + lookup path, which is kept intact below.
 */
const USE_AUTOSUGGEST = true;

// Autosuggest rejects the `types` parameter, so unwanted results have to be dropped here.
// `chainQuery` and `categoryQuery` carry no position at all and would break selection.
const ADDRESS_RESULT_TYPES = ["houseNumber", "street", "intersection", "postalCodePoint"];
const LOCALITY_RESULT_TYPES = [...ADDRESS_RESULT_TYPES, "locality", "administrativeArea"];

// Most autosuggest results for a short query are places we discard — "Roseville" returns 20 and
// keeps 5 — so the pool has to be much wider than the list we show.
const AUTOSUGGEST_FETCH_LIMIT = 20;

export class HereAddressProvider implements IAddressSuggestProvider, IGeocodingProvider {
    public readonly providerId = AutocompleteProvider.HERE;

    private readonly _apiKey: string;
    private readonly _useAutosuggest: boolean;

    constructor(apiKey: string, useAutosuggest: boolean = USE_AUTOSUGGEST) {
        this._apiKey = apiKey;
        this._useAutosuggest = useAutosuggest;
    }

    public async suggestAsync(
        query: string,
        options: AddressSuggestOptions,
        signal: AbortSignal
    ): Promise<AddressSuggestion[]> {
        // Autosuggest rejects a request with no spatial reference outright, so a caller that
        // cannot supply a bias point falls back rather than taking a 400 on every keystroke.
        return this._useAutosuggest && options.at
            ? await this._suggestViaAutosuggestAsync(query, options, signal)
            : await this._suggestViaAutocompleteAsync(query, options, signal);
    }

    private async _suggestViaAutosuggestAsync(
        query: string,
        options: AddressSuggestOptions,
        signal: AbortSignal
    ): Promise<AddressSuggestion[]> {
        const url = new URL(AUTOSUGGEST_URL);
        url.searchParams.set("apiKey", this._apiKey);
        url.searchParams.set("q", query);
        url.searchParams.set("limit", String(AUTOSUGGEST_FETCH_LIMIT));

        // Without this the response carries only a formatted label, and the address form needs
        // the individual components.
        url.searchParams.set("show", "details");

        this._appendSpatialParams(url, options);

        const response = await fetchJsonAsync<{ items?: HereAutosuggestItem[] }>(
            this.providerId,
            url.toString(),
            { signal }
        );

        const allowedTypes = options.mode === "address" ? ADDRESS_RESULT_TYPES : LOCALITY_RESULT_TYPES;

        return (response.items ?? [])
            .filter(item => allowedTypes.includes(item.resultType ?? ""))
            .map(item => this._toResolvedSuggestion(item))
            .filter((suggestion): suggestion is AddressSuggestion => suggestion !== null)
            .slice(0, options.limit);
    }

    private async _suggestViaAutocompleteAsync(
        query: string,
        options: AddressSuggestOptions,
        signal: AbortSignal
    ): Promise<AddressSuggestion[]> {
        const url = new URL(AUTOCOMPLETE_URL);
        url.searchParams.set("apiKey", this._apiKey);
        url.searchParams.set("q", query);
        url.searchParams.set("limit", String(options.limit));

        // The store locator searches by city, state or zip, so only the delivery-address input
        // restricts results to address types. HERE applies this before limit, so the list fills.
        if (options.mode === "address")
            url.searchParams.set("types", "address");

        this._appendSpatialParams(url, options);

        const response = await fetchJsonAsync<{ items?: HereAutocompleteItem[] }>(
            this.providerId,
            url.toString(),
            { signal }
        );

        return (response.items ?? [])
            .map(item => this._toSuggestion(item))
            .filter((suggestion): suggestion is AddressSuggestion => suggestion !== null);
    }

    /**
     * `at` biases ranking; `in=countryCode` is the hard filter that keeps a numeric query from
     * matching postal codes in countries the brand does not operate in. The two combine, but
     * `at` must never be paired with `in=circle` or `in=bbox` — HERE rejects that with a 400.
     */
    private _appendSpatialParams(url: URL, options: AddressSuggestOptions): void {
        if (options.at)
            url.searchParams.set("at", `${options.at.lat},${options.at.lng}`);

        if (options.language)
            url.searchParams.set("lang", options.language);

        const countryCodes = toAlpha3CountryCodes(options.countryCodes ?? []);
        if (countryCodes.length > 0)
            url.searchParams.set("in", `countryCode:${countryCodes.join(",")}`);
    }

    public async resolveAsync(suggestion: AddressSuggestion): Promise<GeoAddress | null> {
        const id = suggestion.providerRef;
        if (typeof id !== "string" || !id)
            return null;

        const url = new URL(LOOKUP_URL);
        url.searchParams.set("apiKey", this._apiKey);
        url.searchParams.set("id", id);

        const lookup = await fetchJsonAsync<HereLookupResponse>(this.providerId, url.toString());

        const position = lookup.position ?? lookup.access?.[0];
        if (!position)
            return null;

        const coords: LatLng = { lat: position.lat, lng: position.lng };

        // The lookup already carries the structured address; reverse geocoding its point can
        // snap to a neighbouring house number, so only fall back when the address is missing.
        if (lookup.address)
            return this._toGeoAddress(coords, lookup.address);

        return await this.reverseGeocodeAsync(coords);
    }

    public async geocodeAsync(query: string): Promise<LatLng | null> {
        const trimmed = query.trim();
        if (!trimmed)
            return null;

        const url = new URL(GEOCODE_URL);
        url.searchParams.set("apiKey", this._apiKey);
        url.searchParams.set("q", trimmed);
        url.searchParams.set("limit", "1");

        const response = await fetchJsonAsync<{ items?: HereGeocodeItem[] }>(this.providerId, url.toString());

        const position = response.items?.[0]?.position;
        if (!position)
            return null;

        return { lat: position.lat, lng: position.lng };
    }

    public async reverseGeocodeAsync(coords: LatLng): Promise<GeoAddress | null> {
        const url = new URL(REVERSE_GEOCODE_URL);
        url.searchParams.set("apiKey", this._apiKey);
        url.searchParams.set("at", `${coords.lat},${coords.lng}`);
        url.searchParams.set("limit", "1");

        const response = await fetchJsonAsync<{ items?: HereRevGeocodeItem[] }>(this.providerId, url.toString());

        const address = response.items?.[0]?.address;
        if (!address)
            return null;

        return this._toGeoAddress(coords, address);
    }

    /**
     * Autosuggest already carries the coordinates and components, so the suggestion resolves
     * itself and the chain never calls back to the provider on selection.
     */
    private _toResolvedSuggestion(item: HereAutosuggestItem): AddressSuggestion | null {
        const label = item.address?.label ?? item.title;

        // `access` is the road-side entry point, which is the better delivery target when a
        // rooftop position is missing
        const position = item.position ?? item.access?.[0];
        if (!label || !position)
            return null;

        return {
            label,
            providerId: this.providerId,
            address: this._toGeoAddress({ lat: position.lat, lng: position.lng }, item.address ?? {})
        };
    }

    private _toSuggestion(item: HereAutocompleteItem): AddressSuggestion | null {
        const label = item.address?.label ?? item.title;
        if (!label || !item.id)
            return null;

        return {
            label,
            providerId: this.providerId,
            providerRef: item.id
        };
    }

    private _toGeoAddress(coords: LatLng, address: HereAddress): GeoAddress {
        const street = address.street ?? undefined;

        return {
            coords,
            label: address.label ?? undefined,

            // HERE reports alpha-3; callers match against alpha-2 country data
            countryCode: toAlpha2CountryCode(address.countryCode),
            countryName: address.countryName ?? undefined,

            // HERE often returns the full name in `state`; sometimes also `stateCode`
            stateCode: address.stateCode ?? undefined,
            stateName: address.state ?? undefined,

            // Prefer city; fall back to district/county for regions that omit city
            city: address.city ?? address.district ?? address.county ?? undefined,

            postalCode: address.postalCode ?? undefined,

            street,
            streetShortName: toStreetShortName(street),

            houseNumber: address.houseNumber ?? undefined,

            district: address.district ?? undefined,
            sublocality: address.subdistrict ?? undefined
        };
    }
}
